"use client";

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  THEME_STORAGE_KEY,
  themePreferenceCookieValue,
  type ThemePreference,
} from "@/lib/theme/theme-preference";

type Theme = ThemePreference;

/** Product is dark-only for now; light/system toggles are disabled in UI. */
const FORCED_RESOLVED: "dark" = "dark";

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "light" | "dark";
  systemTheme: "light" | "dark";
  themes: Theme[];
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const defaultContext: ThemeContextValue = {
  theme: "dark",
  setTheme: () => {},
  resolvedTheme: FORCED_RESOLVED,
  systemTheme: "dark",
  themes: ["dark"],
};

function applyResolvedTheme() {
  const root = document.documentElement;
  root.classList.remove("light");
  root.classList.add("dark");
  root.style.colorScheme = "dark";
  root.style.backgroundColor = "#080808";
}

function persistDarkPreference() {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, "dark");
  } catch {
    /* ignore */
  }
  try {
    document.cookie = themePreferenceCookieValue("dark");
  } catch {
    /* ignore */
  }
}

export function InoraThemeProvider({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);

  useLayoutEffect(() => {
    applyResolvedTheme();
    persistDarkPreference();
    setMounted(true);
  }, []);

  useLayoutEffect(() => {
    if (!mounted) return;
    applyResolvedTheme();
  }, [mounted]);

  /** Kept for API compatibility; always stays dark until light mode ships. */
  const setTheme = useCallback((_next: Theme) => {
    applyResolvedTheme();
    persistDarkPreference();
  }, []);

  const value = useMemo(
    () => ({
      theme: "dark" as Theme,
      setTheme,
      resolvedTheme: FORCED_RESOLVED,
      systemTheme: "dark" as const,
      themes: ["dark"] as Theme[],
    }),
    [setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

/** Drop-in replacement for `next-themes` `useTheme`. */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext) ?? defaultContext;
}
